import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Form, useLoaderData, useRouteLoaderData } from '@remix-run/react';

import List from '~/components/common/List';
import { LegacyAuthModal } from '~/components/legacy-auth-modal';
import Banner from '~/components/Subscribe/Banner';
import FAQs from '~/components/Subscribe/FAQs';
import {
  type Selection,
  useSubscribe } from
'~/components/Subscribe/hooks/use-subscribe';
import Modal from '~/components/Subscribe/Modal';
import PremiumBenefits from '~/components/Subscribe/PremiumBenefits';
import Section, { SectionHeading } from '~/components/Subscribe/Section';
import {
  ModalStatus,
  useSubscribeStore } from
'~/components/Subscribe/store/subscribe.store';
import SubscriptionCard from '~/components/Subscribe/SubscriptionCard';
import {
  CX_LOGIN_SUCCESS,
  type CXLoginSuccessEvent } from
'~/custom-events/CXLoginSuccessEvent';
import { useAction } from '~/hooks/use-action';
import { LoaderProvider } from '~/providers/loader-context';
import ModalProvider, { ModalView } from '~/providers/modal-context';
import { MyOCProvider } from '~/providers/myoc-context';
import { type loader as rootLoader } from '~/root-loader';
import { MyOC } from '~/scripts/myoddschecker/MyOC';
import TrackingActions from '~/services/actions/tracking-actions';
import { type MyOCData } from '~/types/myoc';
import { type SubsProduct } from '~/types/subs-product';
import {
  SUBSCRIPTION_TERM,
  type SubscriptionTerm } from
'~/utils/stripeProductValidators';

import styles from './styles/subscribe.module.css';
import { type loader } from './loader';

export { meta } from './meta';
export { loader } from './loader';
export { action } from './action';

export default function LandingPage() {
  const rootLoaderData = useRouteLoaderData<typeof rootLoader>('root');
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const {
    baseUrl,
    baseUrlWithProtocolAndRepub,
    currentPath,
    deviceType,
    myocConfig,
    myocData,
    repub,
    requestContext,
    showMyOC,
    state,
    user
  } = rootLoaderData;
  const userID = myocData.myOCUser?.userID || '';

  const {
    cancelPayment,
    monthlyProduct,
    productPaymentTerm,
    productPrice,
    redirectPath,
    resourcesUrl,
    subscriptionList,
    verifyPayment,
    yearlyProduct
  } = useLoaderData<typeof loader>();

  const setNextModalStatus = useSubscribeStore(
    (state) => state.setNextModalStatus
  );
  const setModalStatus = useSubscribeStore((state) => state.setModalStatus);

  // Add helper methods to window
  useEffect(() => {
    window.setModalStatus = (status: ModalStatus) => setModalStatus(status);
  }, [setModalStatus]);

  const shouldLogin = !myocData?.isLoggedIn || !myocData?.myOCUser;

  // Ensure we sync with global state
  useEffect(() => {
    if (shouldLogin) {
      setModalStatus(ModalStatus.HIDDEN);
    }
  }, [shouldLogin, setModalStatus]);

  const onCancel = useCallback(() => {
    setModalStatus(ModalStatus.CANCELLED);
  }, [setModalStatus]);

  const onConfirmed = useCallback(() => {
    setNextModalStatus(ModalStatus.CONFIRMED);
  }, [setNextModalStatus]);

  const onError = useCallback(() => {
    setModalStatus(ModalStatus.ERROR);
  }, [setModalStatus]);

  const onSubscribe = useCallback(
    (product: SubsProduct, checkout: (product: SubsProduct) => void) => {
      checkout(product);
    },
    []
  );

  const onVerifying = useCallback(() => {
    setNextModalStatus(ModalStatus.VERIFYING);
  }, [setNextModalStatus]);

  const { subscribe } = useSubscribe({
    cancelPayment,
    onCancel,
    onConfirmed,
    onError,
    onSubscribe,
    onVerifying,
    redirectPath,
    repub,
    shouldLogin,
    userSubscriptions: requestContext.user.subscriptions,
    verifyPayment
  });

  const subscribeMontly = useCallback(
    (userData?: CXLoginSuccessEvent['detail']) => {
      subscribe(monthlyProduct, userData);
    },
    [monthlyProduct, subscribe]
  );

  const subscribeYearly = useCallback(
    (userData?: CXLoginSuccessEvent['detail']) => {
      subscribe(yearlyProduct, userData);
    },
    [subscribe, yearlyProduct]
  );

  const subscribeTerm = useCallback(
    (term: SubscriptionTerm, userData?: CXLoginSuccessEvent['detail']) => {
      term === SUBSCRIPTION_TERM.MONTHLY ?
      subscribeMontly(userData) :
      subscribeYearly(userData);
    },
    [subscribeMontly, subscribeYearly]
  );

  const selectionRef = useRef<Selection | undefined>();

  const handleProductClick = useCallback(
    (selection: Selection) => {
      selectionRef.current = selection;

      shouldLogin ?
      window.openModalAtView(ModalView.LoginForm) :
      subscribeTerm(selection.term);
    },
    [shouldLogin, subscribeTerm]
  );

  const onLoginSuccess = useCallback(
    (e: CustomEvent) => {
      if (!selectionRef.current) return;
      subscribeTerm(selectionRef.current.term, e.detail);
    },
    [subscribeTerm]
  );

  useEffect(() => {
    window.addEventListener(CX_LOGIN_SUCCESS, onLoginSuccess);
    return () => {
      window.removeEventListener(CX_LOGIN_SUCCESS, onLoginSuccess);
    };
  }, [onLoginSuccess]);

  const sendCustomEvent = useMemo(
    () => new TrackingActions().eventTracking,
    []
  );

  const { submitAction } = useAction();

  const renderMyOC = () => {
    if (!showMyOC) return null;

    return (
      <>
				<MyOC />
				<LegacyAuthModal />
			</>);

  };

  return (
    <LoaderProvider
      apiResponse={undefined}
      baseUrl={baseUrl}
      baseUrlWithProtocolAndRepub={baseUrlWithProtocolAndRepub}
      currentPath={currentPath}
      deviceType={deviceType}
      repub={repub}
      state={state}
      user={user}>

			<MyOCProvider
        myocConfig={myocConfig}
        myocData={myocData as MyOCData}
        submit={submitAction}>

				<ModalProvider showMyOC={myocConfig.showMyOC}>
					<main>
						<Modal
              productPaymentTerm={productPaymentTerm}
              productPrice={productPrice}
              redirectPath={redirectPath}
              resourcesUrl={resourcesUrl}
              sendCustomEvent={sendCustomEvent}
              subscriptionList={subscriptionList}
              userId={userID} />

						<Banner>
							<Form className={styles.continueHere} method="post">
								{redirectPath &&
                <input
                  name="redirectPath"
                  type="hidden"
                  value={redirectPath} />

                }
								<p>
									Do you want to continue with limits?{' '}
									<button type="submit">Continue here</button>
								</p>
							</Form>
						</Banner>

						<Section className={styles.heroSection} testId="hero-section">
							<picture>
								<source
                  media="(max-width: 600px)"
                  srcSet={`${resourcesUrl}/assets/img/subscribe/hero-mobile.png`} />

								<img
                  alt=""
                  className={styles.heroImage}
                  data-testid="hero-image"
                  src={`${resourcesUrl}/assets/img/subscribe/hero.png`} />

							</picture>
							<div className={styles.heroTextWrapper}>
								<h1
                  className={`${styles.heroHeading} ${styles['heading--green']}`}>

									JANUARY SALE: GET 20% OFF ODDSCHECKER UNLIMITED
								</h1>
								<h2 className={styles.heroSubHeading}>
									<b>
										<span style={{ textDecoration: 'line-through' }}>
											£19.99
										</span>{' '}
										£15.99/month.
									</b>{' '}
									Use code JAN2025 to unlock 20% off for three months & the road
									to Cheltenham 2025.
								</h2>
								<List
                  hasColumns
                  list={subscriptionList}
                  name="subscriptionList"
                  resourcesUrl={resourcesUrl}
                  testId="subscription-list" />

								<div>
									<div>
										<SubscriptionCard
                      buttonCtaText="Subscribe"
                      cardName="yearlyPrice"
                      handleSubscribe={handleProductClick}
                      product={yearlyProduct}
                      sendCustomEvent={sendCustomEvent}
                      userId={userID} />

										<SubscriptionCard
                      buttonCtaText="Subscribe"
                      cardName="monthlyPrice"
                      handleSubscribe={handleProductClick}
                      product={monthlyProduct}
                      sendCustomEvent={sendCustomEvent}
                      userId={userID} />

									</div>
								</div>
							</div>
						</Section>

						<Section testId="benefits-section">
							<SectionHeading>
								<h2 className={styles.heading}>Why upgrade to Unlimited?</h2>
								<p>
									Unlock unlimited access to our comparison grids across your
									favourite sports, expert tools including market movers and
									price history, daily analysis from our expert tipsters and
									enjoy a smoother reduced ad experience. All for{' '}
									<b>{` ${monthlyProduct.priceFormatted} `}</b>a month. Use code
									OCFREE to unlock a free first month.
								</p>
							</SectionHeading>
							<PremiumBenefits resourcesUrl={resourcesUrl} />
						</Section>

						<FAQs />
						{renderMyOC()}
					</main>
				</ModalProvider>
			</MyOCProvider>
		</LoaderProvider>);

}